import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  BestWorstCategoryDto,
  CategoryDepartmentMeanDto,
  CategoryHistoryDto,
  CategoryMeanDto,
  CategoryQuestionScoresDto,
  DepartmentAverageDto,
  ResponseRateDto,
  SurveyAverageDto,
  SurveyCategoryDto,
  SurveyInsightsDto
} from '../models';
import {
  getMockBestAndWorstCategories,
  getMockCategories,
  getMockCategoryDepartmentMeans,
  getMockCategoryHistory,
  getMockCategoryMean,
  getMockCategoryQuestionScores,
  getMockDepartmentAverages,
  getMockHistoricalAverages,
  getMockInsights,
  getMockResponseRate,
  getMockSurveyAverage
} from '../../mock/work-environment-survey.mock-data';

/**
 * Aggregated survey results: overall, per department, per category, per
 * question, and over time.
 *
 * NOT YET CONNECTED TO THE BACKEND (#6073). Same arrangement as
 * `WorkEnvironmentSurveyService` — mock bodies, real signatures, and the
 * intended endpoint documented on every method.
 */
@Injectable()
export class WorkEnvironmentAnalysisService {
  private readonly API_BASE = `${environment.BASE_URL_V2}/work-environment-survey`;

  private readonly mockLatencyMs = 250;

  /**
   * The categories a survey covers.
   *
   * GET {API_BASE}/surveys/{surveyId}/categories
   */
  getCategories(surveyId: number): Observable<SurveyCategoryDto[]> {
    return of(getMockCategories(surveyId)).pipe(delay(this.mockLatencyMs));
  }

  /**
   * Overall average for one survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/average
   */
  getSurveyAverage(surveyId: number): Observable<SurveyAverageDto> {
    return of(getMockSurveyAverage(surveyId)).pipe(delay(this.mockLatencyMs));
  }

  /**
   * Overall average of every survey the business has run, oldest first — the
   * history line on the overview.
   *
   * GET {API_BASE}/business/{businessId}/averages
   */
  getHistoricalAverages(businessId: number): Observable<SurveyAverageDto[]> {
    return of(getMockHistoricalAverages(businessId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * Average per department for one survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/department-averages
   */
  getDepartmentAverages(surveyId: number): Observable<DepartmentAverageDto[]> {
    return of(getMockDepartmentAverages(surveyId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * How many participants have completed the survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/response-rate
   */
  getResponseRate(surveyId: number): Observable<ResponseRateDto> {
    return of(getMockResponseRate(surveyId)).pipe(delay(this.mockLatencyMs));
  }

  /**
   * The strongest and weakest category of one survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/categories/best-worst
   */
  getBestAndWorstCategories(surveyId: number): Observable<BestWorstCategoryDto> {
    return of(getMockBestAndWorstCategories(surveyId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * Mean score of one category in one survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/categories/{categoryId}/mean
   */
  getCategoryMean(
    surveyId: number,
    categoryId: number
  ): Observable<CategoryMeanDto> {
    return of(getMockCategoryMean(surveyId, categoryId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * Mean score of one category, broken down by department.
   *
   * GET {API_BASE}/surveys/{surveyId}/categories/{categoryId}/department-means
   */
  getCategoryDepartmentMeans(
    surveyId: number,
    categoryId: number
  ): Observable<CategoryDepartmentMeanDto[]> {
    return of(getMockCategoryDepartmentMeans(surveyId, categoryId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * One category's score across every survey, oldest first.
   *
   * GET {API_BASE}/business/{businessId}/categories/{categoryId}/history
   */
  getCategoryHistory(
    businessId: number,
    categoryId: number
  ): Observable<CategoryHistoryDto> {
    return of(getMockCategoryHistory(businessId, categoryId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * Average score of every question in one category, for one survey.
   *
   * GET {API_BASE}/surveys/{surveyId}/categories/{categoryId}/question-scores
   */
  getCategoryQuestionScores(
    surveyId: number,
    categoryId: number
  ): Observable<CategoryQuestionScoresDto> {
    return of(getMockCategoryQuestionScores(surveyId, categoryId)).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * The benchmark index, honesty score, suggestions and respondent comments.
   *
   * Nothing computes these yet — they were hardcoded literals in the AMIL
   * prototype. See `SurveyInsightsDto` before designing the endpoint.
   *
   * GET {API_BASE}/surveys/{surveyId}/insights
   */
  getInsights(surveyId: number): Observable<SurveyInsightsDto> {
    return of(getMockInsights(surveyId)).pipe(delay(this.mockLatencyMs));
  }
}
